import React from "react";
import { useTranslation } from "react-i18next";

interface ResizeHandleProps {
  isResizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onKeyboardResize: (delta: number) => void;
}

const KEYBOARD_STEP = 2; // percentage points per keypress

/**
 * A draggable resize handle for adjusting panel widths.
 * Provides visual feedback during resize operations.
 * Keyboard accessible: left/right arrows adjust width.
 */
export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  isResizing,
  onMouseDown,
  onKeyboardResize,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const delta = e.key === "ArrowLeft" ? -KEYBOARD_STEP : KEYBOARD_STEP;

      onKeyboardResize(delta);
    }
  };

  return (
    <div
      aria-label={t("Resize panel")}
      aria-orientation="vertical"
      className="relative cursor-col-resize flex items-center justify-center focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:outline-none"
      data-testid="resize-handle"
      role="separator"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseDown={onMouseDown}
    >
      {/* Thin vertical line - matches the left divider style */}
      <div className={`w-px h-full bg-divider ${isResizing ? "bg-default-300" : ""}`} />
      {/* Grab handle - centred button with grip lines */}
      <div
        className={`absolute flex flex-col gap-0.5 px-1.5 py-2 rounded transition-colors ${isResizing ? "bg-default-200" : "bg-default-100 hover:bg-default-200"}`}
      >
        <div className="w-1 h-px bg-default-400" />
        <div className="w-1 h-px bg-default-400" />
        <div className="w-1 h-px bg-default-400" />
      </div>
    </div>
  );
};
